import "bootstrap/dist/css/bootstrap.min.css";
import "font-awesome/css/font-awesome.min.css";
import React, { Suspense, lazy, useEffect, useState } from "react";
import "react-image-lightbox/style.css";
import "react-modern-drawer/dist/index.css";
import "react-quill/dist/quill.snow.css";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.min.css";
import "swiper/css";
import "swiper/css/pagination";
import { Get } from "./Axios/AxiosFunctions";
import { Loader } from "./Component/Loader";
import BeforeLoginRoute from "./Helper/BeforeLoginRoute";
import ProtectedRouter from "./Helper/ProtectedRoute";
import "./assets/Styles/style.css";
import "./assets/Styles/table.css";
import { BaseURL } from "./config/apiUrl";
import { updateUser } from "./store/auth/authSlice";
import {
  saveNewNotification,
  savePublicFields,
} from "./store/common/commonSlice";
import { sortProperties } from "./Helper/HelperFunction";

const AllPropertiesFolder = lazy(() =>
  import("./pages/Folders/AllPropertiesFolder")
);
const AllReceiptsFolder = lazy(() =>
  import("./pages/Folders/AllReceiptsFolder")
);
const UnsubmittedTimesheets = lazy(() =>
  import("./pages/UnsubmittedTimesheets")
);
const UpdateAssignment = lazy(() => import("./pages/UpdateAssignment"));
const CreateAssignment = lazy(() => import("./pages/CreateAssignment"));
const ReceiptDetails = lazy(() =>
  import("./pages/Folders/Receipts/ReceiptDetails")
);
const FolderPropertyDetails = lazy(() =>
  import("./pages/Folders/FolderProperties/FolderPropertyDetails")
);
const CustomFolder = lazy(() => import("./pages/CustomFolder"));
const AddEditShoppingList = lazy(() => import("./pages/AddEditShoppingList"));
const ShoppingListDetails = lazy(() => import("./pages/ShoppingListDetails"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Login = lazy(() => import("./pages/Login"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const GetStarted = lazy(() => import("./pages/GetStarted"));
const Assignment = lazy(() => import("./pages/Assignment"));
const Users = lazy(() => import("./pages/Users"));
const CreateUser = lazy(() => import("./pages/CreateUser"));
const ViewUser = lazy(() => import("./pages/ViewUser"));
const Properties = lazy(() => import("./pages/Properties"));
const AddEditProperty = lazy(() => import("./pages/AddEditProperty"));
const Timesheet = lazy(() => import("./pages/Timesheet"));
const Checklist = lazy(() => import("./pages/Checklist"));
const AddEditChecklist = lazy(() => import("./pages/AddEditChecklist"));
const ChecklistDetails = lazy(() => import("./pages/ChecklistDetails"));
const ViewChecklist = lazy(() => import("./pages/ViewChecklist"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AllFolders = lazy(() => import("./pages/Folders/AllFolders"));
const FolderProperties = lazy(() => import("./pages/Folders/FolderProperties"));
const Receipts = lazy(() => import("./pages/Folders/Receipts"));
const Inventory = lazy(() => import("./pages/Inventory"));
const ViewInventory = lazy(() => import("./pages/ViewInventory"));
const CreateInventory = lazy(() => import("./pages/CreateInventory"));
const UpdateUser = lazy(() => import("./pages/UpdateUser"));
const Calender = lazy(() => import("./pages/Calender"));
const ViewProperties = lazy(() => import("./pages/ViewProperties"));
const AssignmentDetails = lazy(() => import("./pages/AssignmentDetails"));
const Settings = lazy(() => import("./pages/Settings"));
const UpdatePassword = lazy(() => import("./pages/UpdatePassword"));
// Cms
const CMSPrivacyPolicy = lazy(() => import("./pages/CMS/PrivacyPolicy"));
const ContactUs = lazy(() => import("./pages/ConactUs"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

function App() {
  const dispatch = useDispatch();
  const { access_token, isLogin, user } = useSelector(
    (state) => state?.authReducer
  );
  const [loading, setLoading] = useState(false);

  const getPublicFields = async () => {
    const publicUrl = BaseURL("admin/all/checklist/property/employees");
    setLoading(true);
    const publicRes = await Get(publicUrl, access_token, false, dispatch);
    if (publicRes !== undefined) {
      const { data } = publicRes?.data;
      const sortedProperties = sortProperties(data?.properties);
      const capitalizeEmployees = data?.employees.map((ele) => {
        return {
          ...ele,
          fullName: ele?.fullName
            ?.toLowerCase()
            .replace(/\b\w/g, (l) => l.toUpperCase()),
        };
      });
      const public_fields = {
        ...data,
        properties: sortedProperties,
        employees: capitalizeEmployees,
      };
      dispatch(savePublicFields(public_fields));
    }
    setLoading(false);
  };

  // get me
  const getUser = async () => {
    const url = BaseURL(`users/Me`);
    const response = await Get(url, access_token, false, dispatch);
    if (response !== undefined) {
      await dispatch(updateUser(response?.data?.data?.user));
    }
  };
  const getNotificationFromSocket = async () => {
    const responseData = await Get(
      BaseURL("notifications"),
      access_token,
      false,
      dispatch
    );
    if (responseData !== undefined) {
      const { unSeenCount } = responseData?.data;
      dispatch(saveNewNotification(unSeenCount));
    }
  };
  useEffect(() => {
    if (isLogin) {
      getUser();
      getPublicFields();
      getNotificationFromSocket();
    }
  }, [isLogin]);

  if (loading) {
    return <Loader className={"vh-100"} />;
  }
  return (
    <>
      <ToastContainer />
      <BrowserRouter>
        <Suspense fallback={<Loader className={"vh-100"} />}>
          <Routes>
            <Route
              exact
              path="/login"
              element={<BeforeLoginRoute element={<Login />} />}
            />
            <Route
              exact
              path="/"
              element={<BeforeLoginRoute element={<GetStarted />} />}
            />
            <Route
              exact
              path="/dashboard"
              element={<ProtectedRouter element={<Dashboard />} />}
            />
            <Route
              exact
              path="/users"
              element={<ProtectedRouter element={<Users />} />}
            />
            <Route
              exact
              path="/users/:slug"
              element={<ProtectedRouter element={<ViewUser />} />}
            />

            <Route
              exact
              path="/create-user"
              element={<ProtectedRouter element={<CreateUser />} />}
            />
            <Route
              exact
              path="/update-user"
              element={<ProtectedRouter element={<UpdateUser />} />}
            />
            <Route
              exact
              path="/assignment"
              element={<ProtectedRouter element={<Assignment />} />}
            />
            <Route
              exact
              path="/add-assignment"
              element={<ProtectedRouter element={<CreateAssignment />} />}
            />
            <Route
              exact
              path="/edit-assignment"
              element={<ProtectedRouter element={<UpdateAssignment />} />}
            />
            <Route
              exact
              path="/assignment/:slug"
              element={<ProtectedRouter element={<AssignmentDetails />} />}
            />
            <Route
              exact
              path="/folders"
              element={<ProtectedRouter element={<AllFolders />} />}
            />
            <Route
              exact
              path="/folders/all-properties"
              element={<ProtectedRouter element={<AllPropertiesFolder />} />}
            />
            <Route
              exact
              path="/folders/all-receipts"
              element={<ProtectedRouter element={<AllReceiptsFolder />} />}
            />
            <Route
              exact
              path="/folders/properties"
              element={<ProtectedRouter element={<FolderProperties />} />}
            />
            <Route
              exact
              path="/folders/custom"
              element={<ProtectedRouter element={<CustomFolder />} />}
            />
            <Route
              exact
              path="/folders/properties/:id"
              element={<ProtectedRouter element={<FolderPropertyDetails />} />}
            />
            <Route
              exact
              path="/folders/receipts"
              element={<ProtectedRouter element={<Receipts />} />}
            />
            <Route
              exact
              path="/folders/receipts/:id"
              element={<ProtectedRouter element={<ReceiptDetails />} />}
            />
            {/* folder---------------- */}
            <Route
              exact
              path="/inventory"
              element={<ProtectedRouter element={<Inventory />} />}
            />
            <Route
              exact
              path="/inventory/:id"
              element={<ProtectedRouter element={<ViewInventory />} />}
            />
            <Route
              exact
              path="/create-inventory"
              element={<ProtectedRouter element={<CreateInventory />} />}
            />
            <Route
              exact
              path="/properties"
              element={<ProtectedRouter element={<Properties />} />}
            />
            <Route
              exact
              path="/add-edit-property"
              element={<ProtectedRouter element={<AddEditProperty />} />}
            />
            <Route
              exact
              path="/properties/:slug"
              element={<ProtectedRouter element={<ViewProperties />} />}
            />

            <Route
              exact
              path="/timesheet"
              element={<ProtectedRouter element={<Timesheet />} />}
            />
            <Route
              exact
              path="/checklist"
              element={<ProtectedRouter element={<Checklist />} />}
            />
            <Route
              exact
              path="/calendar"
              element={<ProtectedRouter element={<Calender />} />}
            />
            <Route
              exact
              path="/checklist/property/:id"
              element={<ProtectedRouter element={<ChecklistDetails />} />}
            />
            <Route
              exact
              path="/checklist/:slug"
              element={<ProtectedRouter element={<ViewChecklist />} />}
            />
            <Route
              exact
              path="/add-edit-checklist"
              element={<ProtectedRouter element={<AddEditChecklist />} />}
            />
            <Route
              exact
              path="/settings"
              element={<ProtectedRouter element={<Settings />} />}
            />
            {/* <Route
              exact
              path='/update-password'
              element={<ProtectedRouter element={<UpdatePassword />} />}
            /> */}
            <Route
              exact
              path="/add-edit-shoppinglist"
              element={<ProtectedRouter element={<AddEditShoppingList />} />}
            />
            <Route
              exact
              path="/shopping-list/:slug"
              element={<ProtectedRouter element={<ShoppingListDetails />} />}
            />
            <Route
              exact
              path="/notifications"
              element={<ProtectedRouter element={<Notifications />} />}
            />

            {/* CMS */}

            <Route
              exact
              path="/cms/privacy-policy"
              element={<ProtectedRouter element={<CMSPrivacyPolicy />} />}
            />
            {/* End CMS */}
            <Route
              exact
              path="/contact-us"
              element={<BeforeLoginRoute element={<ContactUs />} />}
            />
            <Route
              exact
              path="/privacy-policy"
              element={<BeforeLoginRoute element={<PrivacyPolicy />} />}
            />
            <Route
              exact
              path="/unsubmit-timesheets"
              element={<ProtectedRouter element={<UnsubmittedTimesheets />} />}
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </>
  );
}

export default App;
